import "./Toaster.css";

export class Toaster {
  constructor() {
    this.element = null;
    this.messages = []; // FIFO des 10 derniers messages
    this.activeToasts = []; // Toasts actuellement affichés
    this.isHistoryOpen = false;
    this.maxMessages = 10;
    this.autoHideDelay = 1000; // 1 seconde(s)

    this.init();
  }

  init() {
    this.createElement();
    this.attachToBody();
  }

  cleanupOrphanedToasts() {
    // Supprimer tous les toasts du DOM qui ne sont plus dans activeToasts
    const existingToasts = document.querySelectorAll(".toast");
    const activeIds = new Set(this.activeToasts.map((t) => t.id));

    existingToasts.forEach((toastElement) => {
      const id = parseFloat(/** @type {HTMLElement} */ (toastElement).dataset.id);
      if (!activeIds.has(id)) {
        toastElement.remove();
      }
    });
  }

  createElement() {
    this.element = document.createElement("div");
    this.element.className = "toaster-container";
    this.element.innerHTML = `
      <div class="history-floating-icon" title="Historique des messages">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
        </svg>
      </div>
      <div class="toasts-stack"></div>
      <div class="message-history" style="display: none;">
        <div class="history-header">
          <span>Historique des messages</span>
          <button class="close-history" type="button">×</button>
        </div>
        <div class="history-content">
          <div class="no-messages">Aucun message</div>
        </div>
      </div>
    `;

    // Event listeners
    this.element.querySelector(".history-floating-icon").addEventListener("click", () => {
      this.toggleHistory();
    });

    this.element.querySelector(".close-history").addEventListener("click", () => {
      this.hideHistory();
    });

    // Fermer l'historique si on clique à l'extérieur
    document.addEventListener("click", (e) => {
      if (this.isHistoryOpen && !this.element.contains(/** @type {Node} */ (e.target))) {
        this.hideHistory();
      }
    });
  }

  attachToBody() {
    document.body.appendChild(this.element);
  }

  addMessage(type, message) {
    // Nettoyer d'abord les orphelins
    this.cleanupOrphanedToasts();
    // Ajouter un nouveau message
    const timestamp = new Date();
    const messageObj = {
      id: Date.now() + Math.random(),
      type,
      message,
      timestamp,
    };

    // Ajouter à l'historique (FIFO)
    this.messages.push(messageObj);
    if (this.messages.length > this.maxMessages) {
      this.messages.shift(); // Retirer le plus ancien
    }

    // Animation de l'icône flottante
    this.animateIcon();

    // Créer et afficher le toast
    this.showToast(messageObj);

    // Mettre à jour l'historique si ouvert
    if (this.isHistoryOpen) {
      this.updateHistoryContent();
    }
  }

  showToast(messageObj) {
    const toastElement = document.createElement("div");
    toastElement.className = `toast toast-${messageObj.type}`;
    toastElement.dataset.id = messageObj.id;

    toastElement.innerHTML = `
      <div class="toast-content">
        <div class="toast-message">${messageObj.message}</div>
        <button class="toast-close" type="button">×</button>
      </div>
    `;

    toastElement.querySelector(".toast-close").addEventListener("click", (e) => {
      e.stopPropagation();
      this.removeToast(messageObj.id);
    });

    // Ajouter au DOM
    const toastsStack = this.element.querySelector(".toasts-stack");
    toastsStack.appendChild(toastElement);

    // Animation d'entrée
    requestAnimationFrame(() => {
      toastElement.classList.add("toast-show");
    });

    // Ajouter à la liste des toasts actifs
    this.activeToasts.push({
      id: messageObj.id,
      element: toastElement,
      timer: setTimeout(() => {
        this.removeToast(messageObj.id);
      }, this.autoHideDelay),
    });
  }

  removeToast(id) {
    const toastIndex = this.activeToasts.findIndex((toast) => toast.id === id);
    if (toastIndex === -1) return;

    const toast = this.activeToasts[toastIndex];

    // Annuler le timer
    if (toast.timer) {
      clearTimeout(toast.timer);
    }

    // Animation de sortie
    toast.element.classList.add("toast-hide");

    setTimeout(() => {
      if (toast.element.parentNode) {
        toast.element.parentNode.removeChild(toast.element);
      }
      this.activeToasts.splice(toastIndex, 1);
    }, 300); // Durée de l'animation CSS
  }

  toggleHistory() {
    if (this.isHistoryOpen) {
      this.hideHistory();
    } else {
      this.showHistory();
    }
  }

  showHistory() {
    this.isHistoryOpen = true;
    this.updateHistoryContent();
    /** @type {HTMLElement} */ (this.element.querySelector(".message-history")).style.display = "block";

    // Animation
    requestAnimationFrame(() => {
      this.element.querySelector(".message-history").classList.add("history-show");
    });
  }

  hideHistory() {
    this.isHistoryOpen = false;
    const historyElement = this.element.querySelector(".message-history");
    historyElement.classList.remove("history-show");

    setTimeout(() => {
      /** @type {HTMLElement} */ (historyElement).style.display = "none";
    }, 200);
  }

  updateHistoryContent() {
    const historyContent = this.element.querySelector(".history-content");

    if (this.messages.length === 0) {
      historyContent.innerHTML = '<div class="no-messages">Aucun message</div>';
      return;
    }

    const messagesHtml = this.messages
      .slice()
      .reverse()
      .map(
        (msg) => `
        <div class="history-message history-message-${msg.type}">
          <div class="message-header">
            <span class="message-time">${this.formatTime(msg.timestamp)}</span>
          </div>
          <div class="message-content">${msg.message}</div>
        </div>
      `
      )
      .join("");

    historyContent.innerHTML = messagesHtml;
  }

  animateIcon() {
    const icon = this.element.querySelector(".history-floating-icon");
    icon.classList.add("pulse");
    setTimeout(() => {
      icon.classList.remove("pulse");
    }, 600);
  }

  formatTime(timestamp) {
    return timestamp.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  clear() {
    // Supprimer tous les toasts actifs
    this.activeToasts.forEach((toast) => {
      if (toast.timer) {
        clearTimeout(toast.timer);
      }
      if (toast.element.parentNode) {
        toast.element.parentNode.removeChild(toast.element);
      }
    });

    // Réinitialiser les données
    this.activeToasts = [];
    this.messages = [];

    // Fermer l'historique
    this.hideHistory();
  }

  destroy() {
    this.clear();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}
